<!--Copy right - © 2024 tecera gmbh-->
<script>
    import { onDestroy, onMount } from "svelte";
    import {
        selected_device,
        current_device_data,
        current_device_data_additional_values,
        is_loading_current_device_data,
        current_sample_time,
        extended_device_channel_list,
    } from "../stores.js";
    import { slide, fade, fly } from "svelte/transition";

    export let is_mounted;
    export let chart_data;
    export let multiple_devices = false;

    let data_list;
    let channels = [];
    chart_data = [];
    let timestamps = [];
    let data_is_loaded = false;
    const CELL_COLOR = {
        RED: "rgba(255, 0, 0, 0.25)",
        GREEN: "rgba(0, 255, 0, 0.25)",
        TRANSPARENT: "transparent",
    };

    onDestroy(() => {
        chart_data = [];
    });

    function create_data() {
        let combined_data = [];
        channels = $extended_device_channel_list;
        data_list = combined_data.map((data) => {
            let row = [format_date(data.received_at)];
            channels.forEach((channel) => {
                let value = data[channel.name];
                if (channel.boolean_0) {
                    // displaying text instead of boolean
                    value = value === 0 ? channel.boolean_0 : channel.boolean_1;
                }
                row.push(value);
            });
            return row;
        });

        chart_data = channels.map((channel, index) => {
            // checks wether the  config has any data
            let device_data = $current_device_data.find(
                (data) => data.config_id == channel.config,
            );
            device_data = device_data ? device_data.data : null;
            // special case for sum channels here
            if (channel.config === null) {
                device_data = $current_device_data_additional_values;
            }
            let min = null;
            let max = null;
            let series = {
                display_name: channel.display_name,
                data: device_data
                    ? device_data.map((data) => {
                          min == null ||
                          (data[channel.name] != null &&
                              data[channel.name] < min)
                              ? (min = data[channel.name])
                              : null;
                          max == null || data[channel.name] > max
                              ? (max = data[channel.name])
                              : null;
                          return data[channel.name];
                      })
                    : [],
                channel_id: channel.channel_id,
                measurand: channel.measurand,
                visible: channel.visible,
                sub_id: channel.sub_id,
                min: min,
                max: max,
                unit: channel.unit,
            };
            return series;
        });

        timestamps = $current_device_data[0].data.map(
            (data) => data["received_at"],
        );
        data_is_loaded = true;
    }
    function create_hymo_data() {
        let combined_data = [];
        channels = $extended_device_channel_list;
        data_list = combined_data.map((data) => {
            let row = [format_date(data.received_at)];
            channels.forEach((channel) => {
                let value = data[channel.name];
                if (channel.boolean_0) {
                    // displaying text instead of boolean
                    value = value === 0 ? channel.boolean_0 : channel.boolean_1;
                }
                row.push(value);
            });
            return row;
        });
        chart_data = channels.map((channel, index) => {
            // checks wether the  config has any data
            let device_data = $current_device_data;
            let min = null;
            let max = null;
            let series = {
                display_name: channel.display_name,
                data: device_data
                    ? device_data.map((data) => {
                          min == null ||
                          (data[channel.name] != null &&
                              data[channel.name] < min)
                              ? (min = data[channel.name])
                              : null;
                          max == null || data[channel.name] > max
                              ? (max = data[channel.name])
                              : null;
                          return data[channel.name];
                      })
                    : [],
                channel_id: channel.channel_id,
                measurand: channel.measurand,
                visible: channel.visible,
                sub_id: channel.sub_id,
                min: min,
                max: max,
                unit: channel.unit,
                boolean_0: channel.boolean_0,
                boolean_1: channel.boolean_1,
            };
            return series;
        });
        timestamps = $current_device_data.map((data) => data["send_at"]);
        data_is_loaded = true;
    }

    $: if (
        multiple_devices &&
        is_mounted &&
        $selected_device &&
        $selected_device != null &&
        $current_device_data.length > 0 &&
        !is_disabled
    ) {
        create_data();
    }

    $: if (
        !multiple_devices &&
        is_mounted &&
        $selected_device &&
        $selected_device != null &&
        !is_disabled &&
        $current_device_data
    ) {
        create_hymo_data();
    }

    function format_date(input_string) {
        // Create a Date object from the input string
        let date = new Date(input_string);
        // Format the Date object into the desired format
        let formattedString;
        if ($current_sample_time == "Tageswerte") {
            formattedString =
                date.getDate().toString().padStart(2, "0") +
                "." +
                (date.getMonth() + 1).toString().padStart(2, "0") +
                "." +
                date.getFullYear();
        } else {
            formattedString =
                date.getDate().toString().padStart(2, "0") +
                "." +
                (date.getMonth() + 1).toString().padStart(2, "0") +
                "." +
                date.getFullYear() +
                " " +
                date.getHours().toString().padStart(2, "0") +
                ":" +
                date.getMinutes().toString().padStart(2, "0") +
                ":" +
                date.getSeconds().toString().padStart(2, "0");
        }

        return formattedString;
    }

    let is_disabled = false;
    $: {
        if ($selected_device && $selected_device.box_id) {
            is_disabled = true;
        } else {
            is_disabled = false;
        }
    }

    let subtitle = "";
    $: {
        if ($current_sample_time) {
            subtitle =
                $current_sample_time === "Echtzeitwerte"
                    ? " - gemessen"
                    : " - verdichtet";
        }
    }

    let revert_table = ["Magnetring", "Wasserdruck"];
    let values_for_boolean_0 = {
        Magnetring: CELL_COLOR.RED,
        Wasserdruck: CELL_COLOR.RED,
        Batteriestatus: CELL_COLOR.GREEN,
        Hydrantendeckel: CELL_COLOR.GREEN,
    };

    // TODO: Improve this logic and the variables being used by this function
    function determine_cell_colour(channel, idx) {
        if (channel.data[idx] === channel.max && channel.max === channel.min) {
            if (channel.data[idx] === 0) {
                return values_for_boolean_0[channel.measurand];
            } else if (channel.data[idx] === 1) {
                return values_for_boolean_0[channel.measurand] ===
                    CELL_COLOR.RED
                    ? CELL_COLOR.GREEN
                    : CELL_COLOR.RED;
            } else {
                return "transparent";
            }
        } else if (channel.data[idx] === channel.max) {
            return revert_table.includes(channel.measurand)
                ? CELL_COLOR.GREEN
                : CELL_COLOR.RED;
        } else if (channel.data[idx] === channel.min) {
            return revert_table.includes(channel.measurand)
                ? CELL_COLOR.RED
                : CELL_COLOR.GREEN;
        } else {
            return "transparent";
        }
    }
</script>

{#if is_mounted && !is_disabled && $selected_device}
    <div Style="display:flex;flex-direction:column;height:100%;width:100%;">
        <p class="p-kachel" style="flex: 0 0 4%">
            {$current_sample_time + subtitle + " - " + $selected_device.name}
        </p>
        <div class="col-12" style="flex: 1 1 50%;">
            <div class="user_box2" style="overflow:auto;">
                <table class="table table-striped table-sm" id="data_table">
                    <thead class="thead-light" id="data_table_head">
                        <tr>
                            <th scope="col" style="position:sticky;top:-1px;"
                                >Zeitstempel</th
                            >

                            {#each chart_data as channel}
                                {#if channel.visible}
                                    {#if multiple_devices}
                                        <th
                                            scope="col"
                                            style="position:sticky;top:-1px;"
                                            transition:fade|local={{
                                                duration: 200,
                                            }}
                                            >{channel.measurand} ({channel.unit})
                                            <br />
                                            {channel.channel_id < 0
                                                ? "Gesamt"
                                                : channel.sub_id}</th
                                        >
                                    {:else}
                                        <th
                                            scope="col"
                                            style="position:sticky;top:-1px;"
                                            transition:fade|local={{
                                                duration: 200,
                                            }}>{channel.measurand}</th
                                        >
                                    {/if}
                                {/if}
                            {/each}
                        </tr>
                    </thead>
                    {#if !$is_loading_current_device_data || data_is_loaded}
                        <tbody id="customer_table_body">
                            {#each timestamps as timestamp, idx}
                                <tr>
                                    <th style="vertical-align: middle;"
                                        >{format_date(timestamp)}</th
                                    >

                                    {#each chart_data as channel}
                                        {#if channel.visible}
                                            <td
                                                style="border: 1px solid #dee2e6; vertical-align: middle; background-color:{determine_cell_colour(
                                                    channel,
                                                    idx,
                                                )};"
                                                transition:fade|local={{
                                                    duration: 200,
                                                }}
                                                >{channel.data[idx] === null ||
                                                channel.data[idx] === undefined
                                                    ? "--"
                                                    : channel.unit === "status"
                                                      ? channel.data[idx] === 0
                                                          ? channel.boolean_0 +
                                                            " (0)"
                                                          : channel.boolean_1 +
                                                            " (1)"
                                                      : channel.data[
                                                            idx
                                                        ].toFixed(3)}</td
                                            >
                                        {/if}
                                    {/each}
                                </tr>
                            {/each}
                            <!---->
                        </tbody>
                    {:else}
                        <div id="loader" class="loader" style="top:25%;" />
                    {/if}
                </table>
            </div>
        </div>
    </div>
{/if}
